import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export interface SlidingIndicatorBox {
  left: number
  top: number
  width: number
  height: number
  /** False until the first measurement lands, so the box can start hidden
   *  instead of animating in from the origin. */
  ready: boolean
}

const EMPTY_BOX: SlidingIndicatorBox = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  ready: false,
}

/**
 * Measures the selected item in a group so an absolutely-positioned element can
 * slide onto it.
 *
 * Both axes are measured, not just the horizontal one: a group that wraps onto
 * a second row would otherwise strand the indicator on the first. Re-measures on
 * container resize for the same reason — wrapping changes with the width.
 */
export function useSlidingIndicator<TValue extends string>(value: TValue) {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef(new Map<TValue, HTMLElement>())
  const [box, setBox] = useState<SlidingIndicatorBox>(EMPTY_BOX)

  const measure = useCallback(() => {
    const node = itemRefs.current.get(value)
    if (!node) return
    setBox({
      left: node.offsetLeft,
      top: node.offsetTop,
      width: node.offsetWidth,
      height: node.offsetHeight,
      ready: true,
    })
  }, [value])

  useLayoutEffect(measure, [measure])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    const observer = new ResizeObserver(measure)
    // The container catches layout changes around the group (a side panel
    // opening shrinks it with no window resize); the items catch a webfont
    // swap, which re-widths labels without touching the container.
    observer.observe(node)
    for (const item of itemRefs.current.values()) observer.observe(item)
    return () => observer.disconnect()
  }, [measure])

  /** Ref callback for one item, keyed by its value. */
  const register = useCallback(
    (key: TValue) => (node: HTMLElement | null) => {
      if (node) itemRefs.current.set(key, node)
      else itemRefs.current.delete(key)
    },
    [],
  )

  return { containerRef, register, box }
}
